import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";
import { addDays, parseIsoDate, toIsoDate } from "../schedule/scheduleModel";
import { panelDayKey } from "../panel/usePanelDay";
import { supabaseAddComandaLine } from "./useStayComanda";

export interface AddOvernightInput {
  reservationId: string;
  /** Dia do painel, `YYYY-MM-DD` — a saída é no dia seguinte a este. */
  isoDate: string;
  checkoutTime: string | null;
  /** Preço em reais; `null` quando a clínica não configurou. */
  price: number | null;
  /** Venda ABERTA da reserva; `null` quando ainda não existe comanda. */
  saleId: string | null;
  overnightItemName: string;
}

/**
 * Dia seguinte ao calendário de `isoDate`, no mesmo padrão de
 * `parseIsoDate`/`addDays`/`toIsoDate` usado por `scheduleModel.ts` — meio-dia
 * local evita o problema de DST, e `setDate` (dentro de `addDays`) soma dias de
 * calendário em vez de horas, então vira o mês/ano corretamente.
 */
const nextDay = (isoDate: string) => toIsoDate(addDays(parseIsoDate(isoDate), 1));

/**
 * Pernoite marca a reserva e estende a saída para o dia seguinte, no horário
 * limite da clínica. A cobrança vira uma linha na comanda — quando não há
 * comanda aberta ou preço configurado, a reserva é marcada mesmo assim e a
 * cobrança fica para o balcão, em vez de bloquear a operação.
 */
export function useAddStayOvernight() {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useMutation({
    mutationFn: async (input: AddOvernightInput) => {
      const { error } = await supabase
        .from("stay_reservations")
        .update({
          is_overnight: true,
          check_out_date: nextDay(input.isoDate),
          check_out_time: input.checkoutTime,
        })
        .eq("id", input.reservationId);
      if (error) throw error;

      if (input.saleId && input.price != null && input.price > 0) {
        await supabaseAddComandaLine({
          saleId: input.saleId,
          itemName: input.overnightItemName,
          unitPrice: input.price,
        });
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: panelDayKey(clinicId, variables.isoDate) });
      queryClient.invalidateQueries({ queryKey: ["stay-comanda", variables.reservationId] });
      queryClient.invalidateQueries({ queryKey: ["stay-schedule", clinicId] });
    },
  });
}

export interface RemoveOvernightInput {
  reservationId: string;
  /** Dia do painel, para invalidar a consulta certa. */
  isoDate: string;
  /** Nome usado por `useAddStayOvernight` ao lançar a linha — identifica o que apagar. */
  overnightItemName: string;
}

/**
 * Tirar o pernoite desfaz o que `useAddStayOvernight` gravou na reserva: a
 * saída volta para o dia da entrada e o horário limite some.
 */
export function buildRemoveOvernightUpdate(checkInDate: string) {
  return { is_overnight: false as const, check_out_date: checkInDate, check_out_time: null };
}

/**
 * Apaga a linha de pernoite da comanda ABERTA da reserva, se houver uma.
 * Exportada solta (sem estado de hook), no mesmo padrão de
 * `supabaseAddComandaLine`, para ser testável sem `renderHook`.
 */
export async function removeOvernightComandaLine(reservationId: string, overnightItemName: string): Promise<void> {
  const { data: saleRow, error: saleError } = await (supabase as any)
    .from("sales")
    .select("id")
    .eq("stay_reservation_id", reservationId)
    .eq("status", "ABERTA")
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (saleError) throw saleError;
  if (!saleRow) return;

  const { error: deleteError } = await (supabase as any)
    .from("sale_items")
    .delete()
    .eq("sale_id", saleRow.id)
    .eq("item_name", overnightItemName);
  if (deleteError) throw deleteError;
}

/**
 * Tirar o pernoite desfaz a reserva e também a cobrança: se a comanda ainda
 * está ABERTA, a linha do pernoite lançada por `useAddStayOvernight` sai
 * junto. Uma comanda já PAGA fica intocada — quem decide sobre uma cobrança
 * já fechada é o balcão, não esta ação.
 */
export function useRemoveStayOvernight() {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useMutation({
    mutationFn: async (input: RemoveOvernightInput) => {
      const { data, error: readError } = await supabase
        .from("stay_reservations")
        .select("check_in_date")
        .eq("id", input.reservationId)
        .single();
      if (readError) throw readError;

      const { error } = await supabase
        .from("stay_reservations")
        .update(buildRemoveOvernightUpdate(data.check_in_date))
        .eq("id", input.reservationId);
      if (error) throw error;

      await removeOvernightComandaLine(input.reservationId, input.overnightItemName);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: panelDayKey(clinicId, variables.isoDate) });
      queryClient.invalidateQueries({ queryKey: ["stay-comanda", variables.reservationId] });
      queryClient.invalidateQueries({ queryKey: ["stay-schedule", clinicId] });
    },
  });
}
